import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "@/lib/analytics";
import { ArrowRight, Check, ChevronDown, Loader2, Mail, Sparkles } from "lucide-react";
import ProgramPreview from "@/components/landing/ProgramPreview";

const APP_URL = import.meta.env.VITE_APP_URL ?? "https://app.coach-os.eu";
// Dérivé de VITE_APP_URL : en local, la landing tape le Next local et non la prod.
const API_URL =
  import.meta.env.VITE_PROGRAM_API_URL ?? `${APP_URL}/api/public/generate-program`;
const LEAD_URL =
  import.meta.env.VITE_PROGRAM_LEAD_URL ?? `${APP_URL}/api/public/program-lead`;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const CALENDLY_URL = "https://calendly.com/gregory-scafarto/30min";

const EASE = [0.16, 1, 0.3, 1] as const;

const GOALS = [
  { id: "muscle_gain",     label: "Prise de masse" },
  { id: "weight_loss",     label: "Perte de poids" },
  { id: "strength",        label: "Force" },
  { id: "endurance",       label: "Endurance" },
  { id: "general_fitness", label: "Forme générale" },
];

const LEVELS = [
  { id: "beginner",     label: "Débutant" },
  { id: "intermediate", label: "Intermédiaire" },
  { id: "advanced",     label: "Avancé" },
];

const EQUIPMENT = [
  { id: "barbell",    label: "Barre" },
  { id: "dumbbell",   label: "Haltères" },
  { id: "machine",    label: "Machines" },
  { id: "cable",      label: "Poulies" },
  { id: "kettlebell", label: "Kettlebell" },
  { id: "bodyweight", label: "Poids du corps" },
];

const SESSIONS = [2, 3, 4, 5];
const WEEKS = [4, 6, 8];

// Doit rester aligné sur MAX_COACH_NOTES_CHARS côté serveur.
const MAX_NOTES = 600;

// Devant une zone de texte vide, on ne sait pas quoi écrire. Ces amorces montrent
// le niveau de détail attendu — c'est lui qui fait la qualité du programme.
const NOTE_HINTS = [
  { label: "une blessure",    text: "Douleur récurrente à l'épaule droite — éviter le développé militaire." },
  { label: "un point faible", text: "Ischio-jambiers en retard, à rattraper." },
  { label: "peu de temps",    text: "Ne peut pas dépasser 45 minutes par séance." },
];

/** « barre, haltères et machines » — pas « barre, haltères, machines ». */
function listFr(items: string[]) {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}

// ─── Étapes réelles renvoyées par le serveur (SSE) ────────────────────────────
// Le plafond est le pourcentage que le rail ne dépasse PAS tant que l'étape
// suivante n'est pas confirmée. Rien n'est inventé : chaque palier correspond à
// un jalon franchi côté serveur.
interface PhaseDetail {
  count?: number;
  sessions?: string[];
  blocks?: number;
}

const PHASES: {
  id: string;
  label: string;
  ceiling: number;
  /** Ce que l'étape a réellement produit. Affiché à droite du rail. */
  meta?: (d: PhaseDetail) => string | null;
}[] = [
  { id: "started",            label: "Analyse du profil",                ceiling: 12 },
  { id: "exercises_loaded",   label: "Catalogue d'exercices chargé",     ceiling: 24,
    meta: (d) => (d.count ? `${d.count} exercices` : null) },
  { id: "sessions_planned",   label: "Séances planifiées",               ceiling: 38,
    meta: (d) => (d.sessions?.length ? d.sessions.join(" · ") : null) },
  { id: "exercises_selected", label: "Exercices retenus",                ceiling: 48,
    meta: (d) => (d.count ? `${d.count} retenus` : null) },
  { id: "building_program",   label: "Construction de la périodisation", ceiling: 95,
    meta: () => "~40 s" },
];

function clock(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

interface Exercise {
  exercise_id: string;
  exercise_name: string;
  sets_target: number;
  reps_min: number;
  reps_max: number;
  rpe_target: number;
  rest_seconds: number;
  muscle_groups?: string[];
}

interface Session {
  week_number: number;
  day_of_week: number;
  name: string;
  estimated_duration_min: number;
  exercises: Exercise[];
}

interface Block {
  index: number;
  weeks_from: number;
  weeks_to: number;
  focus: string;
  base_rpe: number;
}

interface Program {
  name: string;
  description: string;
  periodization_blocks?: Block[];
  sessions: Session[];
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (t: string) => void;
          theme?: "light" | "dark" | "auto";
          appearance?: "always" | "execute" | "interaction-only";
          size?: "normal" | "flexible" | "compact";
          "error-callback"?: () => boolean | void;
        }
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────

const Pill = ({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex h-[38px] items-center gap-1.5 rounded-full border px-4 text-[13px] font-medium transition-all ${
      active
        ? "border-[#2563EB] bg-[#2563EB] text-white"
        : "border-[#e5e8f0] bg-white text-[#0e1220]/70 hover:border-[#cfd5e4] hover:text-[#0e1220]"
    }`}
  >
    {children}
  </button>
);

/** Choix courts (niveau, séances, durée) : un segmenté ne passe jamais à la ligne. */
function Segmented<T extends string | number>({ options, value, onChange, labels }: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  labels?: (v: T) => string;
}) {
  return (
    <div className="inline-flex rounded-full border border-[#e7e9f1] bg-[#f3f5fb] p-1">
      {options.map((opt) => (
        <button
          key={String(opt)}
          type="button"
          onClick={() => onChange(opt)}
          aria-pressed={opt === value}
          className={`rounded-full px-2.5 py-1.5 text-[13px] sm:px-3.5 font-medium transition-all ${
            opt === value
              ? "bg-[#2563EB] font-semibold text-white"
              : "text-[#0e1220]/55 hover:text-[#0e1220]"
          }`}
        >
          {labels ? labels(opt) : String(opt)}
        </button>
      ))}
    </div>
  );
}

/** Select custom mobile (pas de select natif) : bouton + menu déroulant. */
function MobileSelect<T extends string>({ label, value, options, onChange }: {
  label: string;
  value: T;
  options: readonly { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <div className="relative">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-[#dfe2ec] bg-white px-3 py-2.5 text-left text-sm font-medium text-[#0e1220]"
      >
        <span className="truncate">{current?.label}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#0e1220]/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-xl border border-[#e7e9f1] bg-white py-1 shadow-[0_20px_50px_-20px_rgba(16,20,40,0.25)]">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm ${
                  o.id === value ? "text-[#2563EB]" : "text-[#0e1220]/75"
                }`}
              >
                {o.label}
                {o.id === value && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Multi-select custom mobile : le menu reste ouvert pendant les coches. */
function MobileMultiSelect({ label, values, options, onToggle }: {
  label: string;
  values: string[];
  options: readonly { id: string; label: string }[];
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const picked = options.filter((o) => values.includes(o.id));
  const summary = picked.length ? picked.map((o) => o.label).join(", ") : "Choisir…";
  return (
    <div className="relative">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-[#dfe2ec] bg-white px-3 py-2.5 text-left text-sm font-medium text-[#0e1220]"
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#0e1220]/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-xl border border-[#e7e9f1] bg-white py-1 shadow-[0_20px_50px_-20px_rgba(16,20,40,0.25)]">
            {options.map((o) => {
              const on = values.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onToggle(o.id)}
                  className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm ${
                    on ? "text-[#2563EB]" : "text-[#0e1220]/75"
                  }`}
                >
                  {o.label}
                  {on && <Check className="h-3.5 w-3.5" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** Étiquette en mono : la voix du carnet de coach, pas celle d'un dashboard. */
const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0e1220]/40">
    {children}
  </p>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <Label>{label}</Label>
    {/* Mobile : une seule ligne scrollable (chips façon filtres d'app native) —
        les pastilles ne wrappent plus sur 2-3 lignes. Wrap classique dès md. */}
    <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&>*]:shrink-0">
      {children}
    </div>
  </div>
);

// ─── Progression ──────────────────────────────────────────────────────────────

/**
 * La barre de progression EST le rail qui relie les étapes : une seule figure au
 * lieu d'une barre et d'une liste qui répétaient la même information. Chaque étape
 * affiche ce qu'elle a réellement produit — ces chiffres viennent du flux SSE.
 */
const Progress = ({ pct, phaseIndex, details, elapsed }: {
  pct: number;
  phaseIndex: number;
  details: Record<string, PhaseDetail>;
  elapsed: number;
}) => (
  <div className="rounded-2xl border border-[#e7e9f1] bg-white p-5 shadow-[0_1px_3px_rgba(16,20,40,0.05)] md:p-6">
    {/* entête sobre : titre + chrono en chiffres tabulaires */}
    <div className="flex items-baseline justify-between">
      <p className="text-[15px] font-semibold text-[#0e1220]">Génération en cours…</p>
      <p className="text-xs tabular-nums text-[#0e1220]/45">{clock(elapsed)}</p>
    </div>

    {/* la progression globale tient dans une barre fine — pas de rail vertical */}
    <div className="mt-4 h-1 overflow-hidden rounded-full bg-[#eef0f6]">
      <motion.div
        className="h-full rounded-full bg-[#2563EB]"
        animate={{ width: `${Math.min(pct, 100)}%` }}
        transition={{ ease: "linear", duration: 0.3 }}
      />
    </div>

    <ul className="mt-4 space-y-0.5">
      {PHASES.map((phase, i) => {
        const done = i < phaseIndex;
        const current = i === phaseIndex;
        const meta = phase.meta?.(details[phase.id] ?? {}) ?? null;

        return (
          <li key={phase.id}
            className={`flex items-center gap-3 rounded-lg px-2 py-2 ${current ? "bg-[#eef0f6]" : ""}`}>
            <span className="grid h-4 w-4 shrink-0 place-items-center">
              {done ? (
                <Check className="h-3.5 w-3.5 text-[#2563EB]" strokeWidth={2.5} />
              ) : current ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2563EB]" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-[#d3d7e3]" />
              )}
            </span>
            <span className="flex flex-1 items-baseline justify-between gap-3">
              <span className={`text-sm ${done ? "text-[#0e1220]/45" : current ? "font-medium text-[#0e1220]" : "text-[#0e1220]/35"}`}>
                {phase.label}
              </span>
              {meta && (done || current) && (
                <span className="shrink-0 text-xs text-[#0e1220]/45">{meta}</span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  </div>
);

// ─── Capture d'email — APRÈS le résultat, en échange du PDF ────────────────────

const LeadCapture = ({ leadId, sessionCount }: { leadId: string | null; sessionCount: number }) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false); // jamais pré-coché : un consentement pré-coché est nul
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    track("program_lead_submitted", { marketing_consent: consent });

    try {
      const res = await fetch(LEAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId, email, marketing_consent: consent }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setError(data?.error ?? "L'envoi a échoué. Réessayez.");
        track("program_lead_failed", { status: res.status });
        return;
      }

      setSent(true);
      track("program_lead_sent");
    } catch (err) {
      console.error(`[ProgramGenerator] échec de l'appel à ${LEAD_URL}`, err);
      setError("Connexion impossible. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="mt-6 rounded-2xl border border-[#10b981]/30 bg-[#10b981]/[0.08] p-6 text-center">
        <div className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-[#10b981]/20">
          <Check className="h-5 w-5 text-[#059669]" />
        </div>
        <h4 className="mt-3 text-lg font-bold text-[#0e1220]">C'est envoyé.</h4>
        <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-[#0e1220]/60">
          Le PDF est parti sur <span className="font-semibold text-[#0e1220]/85">{email}</span>.
          Vous pouvez l'imprimer ou le transmettre à votre client tel quel.
        </p>
        {/* Pas de « retrouvez CE programme dans CoachOS » : le programme généré n'est
            pas rattaché au compte à l'inscription. On ne promet que ce qui existe. */}
        <div className="mt-5 flex flex-col items-center gap-2.5">
          <a
            href={`${APP_URL}/signup`}
            onClick={() => track("public_program_cta_clicked", { destination: "signup", from: "lead_sent" })}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition-transform hover:scale-[1.02]"
            style={{ background: "#2563EB" }}
          >
            Créer mon espace CoachOS
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("demo_cta_clicked", { cta: "calendly_demo", location: "program_generator" })}
            className="text-xs text-[#0e1220]/50 underline-offset-2 hover:text-[#0e1220]/80 hover:underline"
          >
            Ou réservez 30 minutes pour qu'on vous le montre
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSend} className="mt-6 rounded-2xl border border-[#2563EB]/25 bg-[#eef1ff] p-6">
      <h4 className="text-lg font-bold text-[#0e1220]">Récupérez ce programme.</h4>
      <p className="mt-1.5 text-sm leading-relaxed text-[#0e1220]/60">
        On vous l'envoie en PDF — {sessionCount} séances, prêt à imprimer ou à transmettre
        à votre client. Pas de carte bancaire, pas de compte à créer.
      </p>

      <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
        <div className="relative flex-1">
          <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#0e1220]/35" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.fr"
            aria-label="Votre adresse email"
            className="h-12 w-full rounded-xl border border-[#dfe2ec] bg-white pl-10 pr-4 text-sm text-[#0e1220] placeholder:text-[#0e1220]/35 focus:border-[#2563EB] focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={sending || !leadId}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl px-6 font-semibold text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
          style={{ background: "#2563EB" }}
        >
          {sending
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</>
            : <>Recevoir le PDF <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>

      <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-[#0e1220]/55">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#d3d7e3] bg-white accent-[#2563EB]"
        />
        Je veux aussi recevoir les conseils produit de CoachOS. Facultatif, et vous pouvez
        vous désabonner en un clic.
      </label>

      {error && (
        <p role="alert" className="mt-3 rounded-xl border border-[#ef4444]/30 bg-[#ef4444]/10 px-4 py-2.5 text-sm text-[#b91c1c]">
          {error}
        </p>
      )}
    </form>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────

const ProgramGenerator = () => {
  const [goal, setGoal] = useState("muscle_gain");
  const [level, setLevel] = useState("intermediate");
  const [sessionsPerWeek, setSessionsPerWeek] = useState(4);
  const [weeks, setWeeks] = useState(8);
  const [equipment, setEquipment] = useState<string[]>(["barbell", "dumbbell", "machine"]);
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [program, setProgram] = useState<Program | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);

  const [pct, setPct] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [details, setDetails] = useState<Record<string, PhaseDetail>>({});
  const [elapsed, setElapsed] = useState(0);

  // 1re génération libre ; la 2e se paie en email ; au-delà, création de compte.
  // C'est le serveur qui tranche (cookie signé) — l'UI ne fait que réagir.
  const [needsEmail, setNeedsEmail] = useState(false);
  const [accountRequired, setAccountRequired] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");

  const turnstileToken = useRef<string | null>(null);
  const turnstileWidget = useRef<string | null>(null);

  /** Le token est à usage unique : après chaque génération, on reset le widget
   *  pour en obtenir un frais (invisible — le conteneur est réaffiché au cas où
   *  Cloudflare exigerait cette fois un challenge interactif). */
  const refreshTurnstile = () => {
    turnstileToken.current = null;
    if (window.turnstile && turnstileWidget.current !== null && turnstileEl.current) {
      turnstileEl.current.style.display = "";
      try { window.turnstile.reset(turnstileWidget.current); } catch { /* widget démonté */ }
    }
  };
  const turnstileEl = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Le rail glisse vers le plafond de l'étape courante sans jamais l'atteindre :
  // il ne peut donc pas annoncer une avance que le serveur n'a pas confirmée.
  useEffect(() => {
    if (!loading) return;
    const ceiling = PHASES[phaseIndex]?.ceiling ?? 95;
    const id = window.setInterval(() => {
      setPct((p) => (p >= ceiling ? p : p + (ceiling - p) * 0.04));
    }, 250);
    return () => window.clearInterval(id);
  }, [loading, phaseIndex]);

  // Le chrono est le seul élément qui a le droit de bouger librement : il ne ment pas.
  useEffect(() => {
    if (!loading) return;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [loading]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileEl.current) return;
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => {
      if (!turnstileEl.current || !window.turnstile) return;
      // interaction-only : le widget reste invisible sauf si Cloudflare exige
      // réellement un challenge — plus de pavé blanc au milieu du formulaire.
      turnstileWidget.current = window.turnstile.render(turnstileEl.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          turnstileToken.current = token;
          // le token est acquis : la boîte « Succès ! » n'apporte rien, on la masque
          if (turnstileEl.current) turnstileEl.current.style.display = "none";
        },
        theme: "light",
        appearance: "interaction-only",
        size: "flexible",
        // en erreur (domaine non autorisé en local, réseau…), Turnstile affiche
        // sa boîte blanche : on la masque — notre message d'erreur stylé suffit.
        "error-callback": () => {
          if (turnstileEl.current) turnstileEl.current.style.display = "none";
          return true;
        },
      });
    };
    document.body.appendChild(script);
    return () => { script.remove(); };
  }, []);

  const toggleEquipment = (id: string) =>
    setEquipment((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);

  const appendHint = (text: string) =>
    setNotes((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text).slice(0, MAX_NOTES));

  // La demande, reformulée telle qu'elle part au modèle.
  const Em = ({ children }: { children: React.ReactNode }) => (
    <span className="font-semibold text-[#0e1220]">{children}</span>
  );
  const equipmentLabels = equipment
    .map((id) => EQUIPMENT.find((e) => e.id === id)?.label.toLowerCase())
    .filter((l): l is string => Boolean(l));

  const briefLine = (
    <>
      un bloc de <Em>{weeks} semaines</Em>, <Em>{sessionsPerWeek} séances</Em> par semaine,{" "}
      <Em>{GOALS.find((g) => g.id === goal)?.label.toLowerCase()}</Em>, niveau{" "}
      <Em>{LEVELS.find((l) => l.id === level)?.label.toLowerCase()}</Em>
      {equipmentLabels.length > 0 && <>, avec <Em>{listFr(equipmentLabels)}</Em></>}
    </>
  );

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    setProgram(null);
    setLeadId(null);
    setPct(0);
    setPhaseIndex(0);
    setDetails({});
    setElapsed(0);
    track("public_program_generate_started", { goal, level, sessions_per_week: sessionsPerWeek, weeks });

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Le compteur de générations vit dans un cookie posé par l'API : sans
        // credentials, le navigateur ne le renverrait pas (origine différente).
        credentials: "include",
        body: JSON.stringify({
          goal, level, equipment,
          duration_weeks: weeks,
          sessions_per_week: sessionsPerWeek,
          session_duration_min: 60,
          coach_notes: notes.trim() || null,
          turnstile_token: turnstileToken.current,
          email: unlockEmail.trim() || null,
        }),
      });

      // Les refus (429, 400, 403…) répondent en JSON, pas en flux.
      if (!res.ok) {
        const data = await res.json().catch(() => null);

        // 2e génération : le serveur réclame un email. On affiche le champ.
        if (data?.code === "email_required") setNeedsEmail(true);
        if (data?.code === "account_required") setAccountRequired(true);

        setError(data?.error ?? "La génération a échoué. Réessayez dans un instant.");
        track("public_program_generate_failed", { status: res.status, code: data?.code });
        return;
      }

      if (!res.body) throw new Error("Réponse sans flux");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Un message SSE se termine par une ligne vide.
        const messages = buffer.split("\n\n");
        buffer = messages.pop() ?? "";

        for (const raw of messages) {
          const event = raw.match(/^event: (.+)$/m)?.[1];
          const dataLine = raw.match(/^data: (.+)$/m)?.[1];
          if (!event || !dataLine) continue;
          const data = JSON.parse(dataLine);

          if (event === "progress") {
            const i = PHASES.findIndex((p) => p.id === data.phase);
            if (i >= 0) {
              setPhaseIndex(i);
              // Ce que l'étape a produit (nb d'exercices, noms des séances…) :
              // c'est ce qui s'affiche à droite du rail.
              const { phase, ...detail } = data as { phase: string } & PhaseDetail;
              setDetails((prev) => ({ ...prev, [phase]: detail }));
            }
          } else if (event === "done") {
            setPct(100);
            setProgram(data.program);
            setLeadId(data.leadId ?? null);
            track("public_program_generated", {
              goal, level,
              sessions: data.program?.sessions?.length ?? 0,
              blocks: data.program?.periodization_blocks?.length ?? 0,
            });
            requestAnimationFrame(() =>
              resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
            );
          } else if (event === "failed") {
            setError(data.error ?? "La génération a échoué.");
            track("public_program_generate_failed", { status: 200, reason: "stream_failed" });
          }
        }
      }
    } catch (err) {
      console.error(`[ProgramGenerator] échec de l'appel à ${API_URL}`, err);
      track("public_program_generate_failed", { status: 0 });
      setError("Connexion impossible. Vérifiez votre réseau et réessayez.");
    } finally {
      refreshTurnstile();
      setLoading(false);
    }
  }

  return (
    <section id="generateur" className="relative z-10 overflow-hidden py-16 md:py-24 " style={{ background: "var(--landing-soft, #F5F7FC)" }}>
      <div className="container relative z-10">
        {/* Deux colonnes tant que le formulaire est là : le titre porte la promesse
            à gauche, le formulaire travaille à droite — plus de long tunnel empilé. */}
        <div className={program ? "" : "grid items-start gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-14"}>
        <motion.div
          initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}
          className={program ? "mb-12 max-w-2xl" : "max-w-2xl lg:order-2 lg:sticky lg:top-28"}
        >
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#2563EB]">Essayez maintenant</p>
          <h2 className="landing-section-title text-3xl font-extrabold leading-tight tracking-tight text-[#0e1220] md:text-5xl">
            Générez un vrai programme.{" "}
            <span className="text-[#2563EB]">
              Sans créer de compte.
            </span>
          </h2>
          <p className="mt-4 max-w-xl text-[#0e1220]/60">
            Décrivez un de vos clients dans le formulaire à côté, puis cliquez sur{" "}
            <strong className="font-semibold text-[#0e1220]">Générer mon programme</strong>. Le programme
            périodisé complet s'affiche à l'écran en moins d'une minute — aucune carte bancaire, aucun compte à créer.
          </p>

          {/* Réassurance : pensée pour équilibrer la colonne sticky desktop —
              sur mobile elle repousse le formulaire de 450 px, on la masque. */}
          {!program && (
            <div className="mt-9 hidden border-t border-[#e7e9f1] lg:block">
              {[
                ["~45 s", "pour un bloc périodisé complet"],
                ["0 compte", "ni carte bancaire, ni email pour le premier essai"],
                ["À l'écran", "le programme s'affiche ici, pas dans votre boîte mail"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-4 border-b border-[#e7e9f1] py-4">
                  <span className="w-24 shrink-0 font-mono text-sm font-semibold text-[#2563EB]">{k}</span>
                  <span className="text-sm text-[#0e1220]/60">{v}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Le formulaire s'efface dès qu'un programme existe : il a fait son travail,
            et il volerait la place au seul écran qui doit convaincre. */}
        {!program && (
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: EASE }}
          className="relative overflow-hidden rounded-[1.5rem] border border-[#eaecf3] p-5 shadow-[0_1px_2px_rgba(16,20,40,0.04)] md:p-6 lg:order-1"
          style={{ background: "#ffffff" }}
        >
          {/* filet lumineux en tête */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: "linear-gradient(90deg, transparent, rgba(37, 99, 235,0.45), transparent)" }} />

          <div className="space-y-4 md:space-y-5">
            {/* ── Mobile : tout en selects homogènes — 2 lignes de 2 + Matériel ── */}
            <div className="space-y-3 md:hidden">
              <div className="grid grid-cols-2 gap-3">
                <MobileSelect label="Objectif" value={goal} options={GOALS} onChange={setGoal} />
                <MobileSelect label="Niveau" value={level} options={LEVELS} onChange={setLevel} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <MobileSelect
                  label="Séances / semaine"
                  value={String(sessionsPerWeek)}
                  options={SESSIONS.map((n) => ({ id: String(n), label: `${n} séances` }))}
                  onChange={(v) => setSessionsPerWeek(Number(v))}
                />
                <MobileSelect
                  label="Durée"
                  value={String(weeks)}
                  options={WEEKS.map((w) => ({ id: String(w), label: `${w} semaines` }))}
                  onChange={(v) => setWeeks(Number(v))}
                />
              </div>
              <MobileMultiSelect label="Matériel" values={equipment} options={EQUIPMENT} onToggle={toggleEquipment} />
            </div>

            {/* Le cadre : des choix faciles, pour engager avant de demander l'effort. */}
            <div className="hidden md:block">
              <Field label="Objectif">
                {GOALS.map((g) => (
                  <Pill key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)}>{g.label}</Pill>
                ))}
              </Field>
            </div>

            {/* Les trois segmentés tiennent sur une ligne : compacts par construction. */}
            <div className="hidden flex-wrap gap-x-4 gap-y-3 md:flex md:gap-x-6 md:gap-y-4">
              <div className="hidden md:block">
                <Label>Niveau</Label>
                <Segmented<string>
                  options={LEVELS.map((l) => l.id)}
                  value={level}
                  onChange={(v) => setLevel(v)}
                  labels={(id) => LEVELS.find((l) => l.id === id)?.label ?? id}
                />
              </div>
              {/* mobile : Séances et Durée partagent la même ligne (scrollable si
                  besoin) ; md:contents rend le wrapper transparent sur desktop */}
              <div className="no-scrollbar flex w-full gap-4 overflow-x-auto md:contents">
                <div className="shrink-0">
                  <Label>Séances / semaine</Label>
                  <Segmented<number>
                    options={SESSIONS}
                    value={sessionsPerWeek}
                    onChange={(v) => setSessionsPerWeek(v)}
                  />
                </div>
                <div className="shrink-0">
                  <Label>Durée</Label>
                  <Segmented<number>
                    options={WEEKS}
                    value={weeks}
                    onChange={(v) => setWeeks(v)}
                    labels={(w) => `${w} sem.`}
                  />
                </div>
              </div>
            </div>

            {/* Six pastilles : en demi-colonne elles cassaient sur trois lignes. */}
            <div className="hidden md:block">
              <Field label="Matériel">
                {EQUIPMENT.map((e) => (
                  <Pill key={e.id} active={equipment.includes(e.id)} onClick={() => toggleEquipment(e.id)}>
                    {e.label}
                  </Pill>
                ))}
              </Field>
            </div>

            {/* ── Le prompt, en climax. C'est lui qui fait la qualité du programme :
                  il n'est plus une note de bas de page intitulée « contraintes ». ── */}
            <div className="rounded-2xl border border-[#dfe2ec] bg-[#f6f7fb] p-3.5 transition-colors focus-within:border-[#2563EB]/60 focus-within:bg-white">
              <label htmlFor="notes" className="mb-2 block text-[15px] font-bold text-[#0e1220] sm:mb-0">
                Décrivez votre client
              </label>
              <p className="mb-3 mt-0.5 hidden text-[13px] leading-relaxed text-[#0e1220]/55 sm:block">
                C'est ce qui sépare un programme générique d'un programme qui lui va.
              </p>

              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
                rows={2}
                placeholder="Ex : Marie, 34 ans, 2 ans de pratique. Douleur récurrente à l'épaule droite — éviter le développé militaire. Veut du haut du corps sans grossir des jambes."
                className="w-full resize-none border-0 bg-transparent p-0 text-[15px] leading-relaxed text-[#0e1220] outline-none placeholder:text-[#0e1220]/35"
              />

              {/* Amorces : devant une zone vide, on ne sait pas quoi écrire. */}
              <div className="no-scrollbar -mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 sm:mx-0 sm:mt-3.5 sm:flex-wrap sm:overflow-visible sm:px-0 [&>*]:shrink-0">
                {NOTE_HINTS.map((hint) => (
                  <button
                    key={hint.label}
                    type="button"
                    onClick={() => appendHint(hint.text)}
                    className="rounded-full bg-[#eef1ff] px-3 py-1 text-xs font-medium text-[#2563EB]/90 transition-colors hover:bg-[#2563EB] hover:text-white"
                  >
                    <span>+</span> {hint.label}
                  </button>
                ))}
              </div>

              <p className="mt-2.5 hidden text-right font-mono text-[11px] text-[#0e1220]/35 sm:block">
                {notes.length} / {MAX_NOTES}
              </p>
            </div>

            {/* ── Signature : la demande, telle qu'elle part au modèle. Le coach voit
                  sa contrainte reprise en cyan — la preuve que son texte compte.
                  Masquée sur mobile : simple récap, elle rallongeait le tunnel. ── */}
            <div className="hidden border-t border-[#e7e9f1] pt-4 sm:flex sm:flex-row sm:gap-4">
              <span className="shrink-0 pt-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0e1220]/40">
                Vous demandez
              </span>
              <p className="text-[15px] leading-relaxed text-[#0e1220]/60">
                {briefLine}
                {notes.trim() && (
                  <>
                    {" — "}
                    <span className="font-semibold text-[#2563EB]">en tenant compte de vos consignes</span>
                  </>
                )}
              </p>
            </div>

            <div ref={turnstileEl} className="flex justify-center empty:hidden" />

            {/* 2e génération : le mur d'email. Il arrive APRÈS que le coach ait déjà
                reçu un programme complet — on demande quand on a donné. */}
            {needsEmail && !accountRequired && (
              <div className="rounded-2xl border border-[#2563EB]/30 bg-[#eef1ff] p-5">
                <p className="text-[15px] font-bold text-[#0e1220]">Un second programme ?</p>
                <p className="mt-1 text-[13px] leading-relaxed text-[#0e1220]/60">
                  Laissez votre email et on vous en génère un autre — le dernier avant la création de compte.
                </p>
                <div className="relative mt-3.5">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#0e1220]/35" />
                  <input
                    type="email"
                    value={unlockEmail}
                    onChange={(e) => setUnlockEmail(e.target.value)}
                    placeholder="vous@exemple.fr"
                    aria-label="Votre adresse email"
                    className="h-12 w-full rounded-xl border border-[#dfe2ec] bg-white pl-10 pr-4 text-sm text-[#0e1220] placeholder:text-[#0e1220]/35 focus:border-[#2563EB] focus:outline-none"
                  />
                </div>
              </div>
            )}

            {loading ? (
              <Progress pct={pct} phaseIndex={phaseIndex} details={details} elapsed={elapsed} />
            ) : accountRequired ? (
              <a
                href={`${APP_URL}/signup`}
                onClick={() => track("public_program_cta_clicked", { destination: "signup", from: "limit_reached" })}
                className="group inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] font-semibold text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition hover:bg-[#1d4ed8]"
              >
                Créer mon compte pour générer sans limite
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={needsEmail && !unlockEmail.trim()}
                className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] font-semibold text-white shadow-[0_8px_20px_-6px_rgba(37,99,235,0.4)] transition hover:bg-[#1d4ed8] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                Générer mon programme
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            )}

            {error && !loading && (
              <p role="alert" className="rounded-xl border border-[#ef4444]/30 bg-[#ef4444]/10 px-4 py-3 text-sm text-[#b91c1c]">
                {error}
              </p>
            )}
          </div>
        </motion.div>
        )}
        </div>

        <AnimatePresence>
          {program && (
            <motion.div
              ref={resultRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              // Large comme la modale du produit (max-w-7xl) : la colonne de droite
              // a besoin de place, sinon elle passe sous le graphe.
              className="mx-auto max-w-7xl scroll-mt-24"
            >
              <ProgramPreview program={program} />

              <div className="mx-auto max-w-3xl">
                <LeadCapture leadId={leadId} sessionCount={program.sessions.length} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProgramGenerator;
